import React, { useState } from 'react';
import { api } from '../../lib/api';
import './AdminBookings.css'; // We'll create this CSS file

const AdminBookingDetailsModal = ({ booking, onClose, onRefresh }) => {
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);

    // Reassignment states
    const [isSwapping, setIsSwapping] = useState(false);
    const [eligibleArtisans, setEligibleArtisans] = useState([]);
    const [isLoadingEligible, setIsLoadingEligible] = useState(false);
    const [selectedArtisanId, setSelectedArtisanId] = useState("");

    if (!booking) return null;

    const handleStatusUpdate = async (newStatus, reason) => {
        if (!window.confirm(`Are you sure you want to mark this booking as ${newStatus}?`)) return;

        setIsUpdating(true);
        try {
            const res = await api.patch(`/api/admin/bookings/${booking.booking_id}/status`, {
                status: newStatus,
                reason: reason
            });

            if (!res.ok) throw new Error("Failed to update status");

            onRefresh();
            onClose();
        } catch (err) {
            alert(err.message);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleForceCancel = () => {
        if (!cancelReason) {
            alert("Please provide a reason for cancellation.");
            return;
        }
        handleStatusUpdate("CANCELLED", cancelReason);
    };

    const handleStartSwap = async () => {
        setIsSwapping(true);
        setIsLoadingEligible(true);
        try {
            const res = await api.get(`/api/admin/bookings/${booking.booking_id}/eligible-artisans`);
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                console.error("Eligible Artisans Error:", errorData);
                throw new Error(JSON.stringify(errorData) || "Failed to fetch available artisans");
            }
            const data = await res.json();
            setEligibleArtisans(data);
        } catch (err) {
            console.error(err);
            alert(err.message);
            setIsSwapping(false);
        } finally {
            setIsLoadingEligible(false);
        }
    };

    const handleReassign = async () => {
        if (!selectedArtisanId) {
            alert("Please select a replacement artisan.");
            return;
        }

        const artisan = eligibleArtisans.find(a => a.artisan_id === selectedArtisanId);
        if (!window.confirm(`Reassign this booking to ${artisan.name}? This will reset status to PENDING_APPROVAL.`)) return;

        setIsUpdating(true);
        try {
            const res = await api.patch(`/api/admin/bookings/${booking.booking_id}/reassign`, {
                new_artisan_id: selectedArtisanId
            });

            if (!res.ok) throw new Error("Failed to reassign booking");

            alert(`Booking reassigned. Sent to ${artisan.name} for approval.`);
            onRefresh();
            onClose();
        } catch (err) {
            alert(err.message);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content booking-modal">
                <div className="modal-header">
                    <h3>Booking Details</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    {/* Reassignment Panel takes over if isSwapping */}
                    {isSwapping ? (
                        <div className="swap-panel">
                            <h4>Select a Replacement Artisan</h4>
                            <p className="swap-hint">Only artisans available at this exact time and location with matching skills are shown below.</p>

                            {isLoadingEligible ? (
                                <div className="loading-placeholder">Checking availability...</div>
                            ) : eligibleArtisans.length === 0 ? (
                                <div className="empty-state">
                                    <p><strong>No Replacements Found.</strong></p>
                                    <p>No other Artisans have the required skills, cover this location, or are free at this exact time.</p>
                                    <button className="btn-secondary" onClick={() => setIsSwapping(false)}>Close</button>
                                </div>
                            ) : (
                                <div className="artisan-selector">
                                    <div className="radio-list">
                                        {eligibleArtisans.map(artisan => (
                                            <label key={artisan.artisan_id} className="radio-item">
                                                <input
                                                    type="radio"
                                                    name="replacement"
                                                    value={artisan.artisan_id}
                                                    checked={selectedArtisanId === artisan.artisan_id}
                                                    onChange={(e) => setSelectedArtisanId(e.target.value)}
                                                />
                                                <span className="artisan-name">{artisan.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="panel-actions">
                                        <button
                                            className="btn-primary"
                                            onClick={handleReassign}
                                            disabled={isUpdating || !selectedArtisanId}
                                        >
                                            Confirm Reassignment
                                        </button>
                                        <button className="btn-secondary" onClick={() => setIsSwapping(false)}>Back</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="booking-summary-card">
                                <div className="status-badge" data-status={booking.status}>{booking.status}</div>
                                <h4>{booking.package_name}</h4>
                                <p>{booking.date} @ {booking.start_time}</p>
                                <p className="price-tag">${(booking.total_cents / 100).toFixed(2)}</p>
                            </div>

                            <div className="details-grid">
                                <div className="detail-section">
                                    <h5>Customer</h5>
                                    <p><strong>Name:</strong> {booking.customer.name}</p>
                                    <p><strong>Email:</strong> {booking.customer.email}</p>
                                    {booking.notes && <p><strong>Notes:</strong> {booking.notes}</p>}
                                </div>

                                <div className="detail-section">
                                    <h5>Artisan</h5>
                                    <p><strong>Name:</strong> {booking.artisan.name}</p>
                                    <p><strong>Email:</strong> {booking.artisan.email}</p>
                                </div>

                                <div className="detail-section full-width">
                                    <h5>Location</h5>
                                    {booking.raw_booking.location ? (
                                        <>
                                            <p>{booking.raw_booking.location.address}</p>
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${booking.raw_booking.location.lat},${booking.raw_booking.location.lng}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="map-link"
                                            >
                                                View on Google Maps
                                            </a>
                                        </>
                                    ) : (
                                        <p>No location data.</p>
                                    )}
                                </div>

                                {booking.raw_booking.adminNotes && booking.raw_booking.adminNotes.length > 0 && (
                                    <div className="detail-section full-width admin-notes">
                                        <h5>Audit Log</h5>
                                        <ul>
                                            {booking.raw_booking.adminNotes.map((note, i) => (
                                                <li key={i}>{note}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className="modal-actions">
                                <div className="override-controls">
                                    <h4>Admin Overrides</h4>

                                    {isCancelling ? (
                                        <div className="cancel-form">
                                            <textarea
                                                placeholder="Reason for cancellation (backend logic will notify users)..."
                                                value={cancelReason}
                                                onChange={(e) => setCancelReason(e.target.value)}
                                            />
                                            <div className="cancel-actions">
                                                <button
                                                    className="btn-danger"
                                                    onClick={handleForceCancel}
                                                    disabled={isUpdating}
                                                >
                                                    Confirm Force Cancel
                                                </button>
                                                <button
                                                    className="btn-secondary"
                                                    onClick={() => setIsCancelling(false)}
                                                >
                                                    Back
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="action-buttons">
                                            <button
                                                className="btn-primary"
                                                onClick={handleStartSwap}
                                            >
                                                Swap Artisan (Rescue)
                                            </button>

                                            <button
                                                className="btn-danger-outline"
                                                onClick={() => setIsCancelling(true)}
                                            >
                                                Force Cancel Booking
                                            </button>

                                            <button
                                                className="btn-success"
                                                onClick={() => handleStatusUpdate("COMPLETED", "Admin marked as Completed")}
                                                disabled={isUpdating}
                                            >
                                                Mark as Completed
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminBookingDetailsModal;
